import { EFinancingProgram } from '@if/core/types'
import { ValueListDto, ValueListItem, ValueListItemDto } from '@src/types/ValueListDto'
import { MutationFunction, QueryClient, QueryFunctionContext, useMutation } from '@tanstack/react-query'
import { getApiClient } from '../api-client'

const SCOPE = 'cancel-reasons'

const keysFactory = {
  cancelReasons: () => [{ scope: SCOPE }] as const,
}

export interface CustomerFeedback {
  withdrawalReasonId: string | null
  contactClient: boolean
  isSatisfied: boolean
  badReviewReasonId: string | null
}

export type CustomerFeedbackDto = CustomerFeedback & {
  creditApplicationId: string
  financingProgramId: EFinancingProgram
}
const submitFeedback: MutationFunction<CustomerFeedbackDto, CustomerFeedbackDto> = async (dto: CustomerFeedbackDto) => {
  const apiClient = getApiClient()
  const response = await apiClient.post(
    `CustomerFeedback/${dto.financingProgramId}/${dto.creditApplicationId}/CustomerFeedback`,
    dto,
  )
  const ret = response.data as CustomerFeedbackDto
  ret.creditApplicationId = dto.creditApplicationId
  return ret
}

export function useSubmitFeedback(): [
  MutationFunction<CustomerFeedbackDto, CustomerFeedbackDto>,
  boolean,
  boolean,
  () => void,
] {
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: submitFeedback,
    onSuccess: (data) => {
      return data
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

const getCancelReasons = async ({
  // eslint-disable-next-line no-empty-pattern
  queryKey: [],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['cancelReasons']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<ValueListDto>(`/CustomerFeedback/WithdrawalReasons`)

  return response.data
}

function buildValueList(list: ValueListDto | undefined, lang: string) {
  if (!list) return []

  const inputList: ValueListItemDto[] = [...list.items]
  if (list.sortMethod === 'priority') inputList.sort((x) => x.priority ?? 0)

  const items: ValueListItem[] = inputList.map((x) => {
    return {
      id: x.value,
      text: lang === 'fr' ? x.textFr : x.textEn,
      contactClient: x.contactClient,
    }
  })
  if (list.sortMethod === 'alpha') items.sort((one, two) => (one.text > two.text ? -1 : 1))
  return items
}

const cancelReasonsQueryOptions = {
  queryKey: keysFactory.cancelReasons(),
  queryFn: getCancelReasons,
}

export const fetchCancelReasons = (queryClient: QueryClient, lang: string): Promise<ValueListItem[]> =>
  queryClient.ensureQueryData(cancelReasonsQueryOptions).then((data) => buildValueList(data, lang))
